import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useHotkey } from '@modules/common/hooks';
import { useUpdateTenant } from '@modules/common/hooks/useUpdateTenant';
import { useDuplicateAndUpdateDashboard } from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { GetUserByEmailDocument, Scope } from 'generated/graphql';
import { useGetUser, useGetUserSetter } from 'v2/core/services/user';

export const SetCopyAsDefaultHomeDialogContent = () => {
  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const { closeModal } = useMuiModalContext();

  const user = useGetUser();

  const dashboard = useDashboardStore(({ dashboard }) => dashboard);

  const [
    duplicateAndUpdateDashboard,
    { loading: isDuplicateDashboardInProgress },
  ] = useDuplicateAndUpdateDashboard();

  const { updateTenant, loading: isUpdateTenantInProgress } = useUpdateTenant({
    refetchQueries: [GetUserByEmailDocument],
  });

  const appSetUser = useGetUserSetter();

  const isLoading = isDuplicateDashboardInProgress || isUpdateTenantInProgress;

  const setAsDefaultHome = async () => {
    try {
      const duplicatedDashboard = await duplicateAndUpdateDashboard({
        duplicateAndUpdateDashboardInput: {
          dashboardID: dashboard.id,
          shouldDuplicateAnalyses: true,
          updateParams: {
            scope: Scope.Hidden,
          },
        },
      });

      if (duplicatedDashboard) {
        await updateTenant({
          dashboardSettings: {
            homepageDashboardID: duplicatedDashboard.id,
          },
        });

        appSetUser({
          ...user,
          tenant: {
            ...user.tenant,
            dashboardSettings: {
              ...user.tenant.dashboardSettings,
              homepageDashboardID: duplicatedDashboard.id,
            },
          },
        });

        showSuccessNotification({
          contentComponent:
            'Successfully set this Dashboard as the default Home.',
          props: {},
        });
        closeModal();
      }
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to set Dashboard as the default Home.',
        props: {},
      });
    }
  };

  useHotkey({
    hotkey: 'Enter',
    onHotkey: setAsDefaultHome,
    actionTitle: 'Set as Default Home',
  });

  return (
    <>
      <ModalHeader title="Set this Dashboard's copy as the default Home?" />
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body1">
            This action will replace your organization's current default Home
            with a copy of this Dashboard. The default Home is the first view
            new users see after signing up.
          </Typography>
          <Alert severity="info">
            Future changes to this Dashboard will not automatically reflect in
            the default Home.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" m={1} spacing={2}>
          <Button
            onClick={() => closeModal()}
            variant="outlined"
            endIcon={<EscHotKeyIcon />}
          >
            Cancel
          </Button>
          <LoadingButton
            autoFocus
            loading={isLoading}
            onClick={setAsDefaultHome}
            variant="contained"
            endIcon={<ReturnHotKeyIcon isDisabled={isLoading} />}
          >
            Confirm & Replace
          </LoadingButton>
        </Stack>
      </DialogActions>
    </>
  );
};
